/**
 * Templates: the organization's own standards a document can be checked
 * against — a list of requirements, each with the severity a miss carries.
 * Members read them; admins write them (the server checks again). With none
 * yet, an admin can start from a fund-administration starter.
 *
 * @module components/apps/contractIntelligence/CiTemplatesPage
 */
import { ClipboardListIcon, PencilIcon, PlusIcon, Trash2Icon, XIcon } from "lucide-react";
import { useState } from "react";

import { cn } from "../../../lib/utils";
import {
  archiveCiTemplate,
  CI_SEVERITIES,
  saveCiTemplate,
  useCiTemplates,
  type CiSeverity,
  type CiTemplate,
} from "../../../state/contractIntelligence";
import { SeverityGlyph } from "../../regulation/finding/findingGlyphs";
import { Button } from "../../ui/button";
import { Dialog, DialogClose, DialogDescription, DialogFooter, DialogHeader, DialogPanel, DialogPopup, DialogTitle } from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../../ui/select";
import { Textarea } from "../../ui/textarea";
import { toastManager } from "../../ui/toast";
import { SEVERITY_LABEL } from "./ciModel";
import { CiConfirmDialog, CiEmpty, CiListSkeleton, CiPage, CiUnavailable } from "./ciParts";
import { useCiOrg } from "./useCiOrg";

interface DraftItem {
  readonly key: string;
  readonly id?: string;
  readonly title: string;
  readonly requirement: string;
  readonly severity: CiSeverity;
}

interface Draft {
  readonly templateId: string | null;
  readonly name: string;
  readonly documentType: string;
  readonly description: string;
  readonly items: ReadonlyArray<DraftItem>;
}

let keySeed = 0;
const newKey = () => `item-${(keySeed += 1)}`;

/** Starters for the documents fund administrators see most; an admin edits them before saving. */
const STARTERS: ReadonlyArray<Omit<Draft, "templateId">> = [
  {
    name: "Side letter standard",
    documentType: "Side letter",
    description: "What a side letter may grant an investor without board approval.",
    items: [
      { key: newKey(), title: "Fee terms need board approval", requirement: "Any rebate, waiver or reduction of management or performance fees is subject to prior board approval and is disclosed to other investors where required.", severity: "high" },
      { key: newKey(), title: "No unrestricted most-favoured-nation clause", requirement: "Most-favoured-nation rights are limited to investors with an equal or smaller commitment and exclude terms granted for legal, tax or regulatory reasons.", severity: "medium" },
      { key: newKey(), title: "Confidentiality preserved", requirement: "The investor may disclose the letter's terms only to its professional advisers or as required by law or regulation.", severity: "medium" },
      { key: newKey(), title: "Enhanced reporting stays within policy", requirement: "Additional transparency or reporting does not give the investor portfolio information earlier or in more detail than other investors, unless the board approves.", severity: "high" },
    ],
  },
  {
    name: "Administration agreement standard",
    documentType: "Administration agreement",
    description: "Terms the firm requires when it appoints or acts as an administrator.",
    items: [
      { key: newKey(), title: "Delegation needs prior consent", requirement: "The administrator may delegate its functions only with the fund's prior written consent, and remains liable for its delegates.", severity: "high" },
      { key: newKey(), title: "Liability standard is negligence", requirement: "The administrator is liable for loss caused by its negligence, wilful default or fraud — not fraud alone.", severity: "critical" },
      { key: newKey(), title: "AML/CFT records and access", requirement: "Customer due diligence records are kept for at least five years after the relationship ends and are available to the firm and the regulator on request.", severity: "high" },
      { key: newKey(), title: "Termination notice", requirement: "Either party may terminate on no less than 90 days' written notice, with an orderly hand-over of records.", severity: "low" },
    ],
  },
];

const fromTemplate = (template: CiTemplate): Draft => ({
  templateId: template.id,
  name: template.name,
  documentType: template.documentType ?? "",
  description: template.description ?? "",
  items: template.items.map((item) => ({ ...item, key: newKey() })),
});

const blank = (): Draft => ({
  templateId: null,
  name: "",
  documentType: "",
  description: "",
  items: [{ key: newKey(), title: "", requirement: "", severity: "medium" }],
});

function TemplateEditor({
  organizationId,
  draft,
  onClose,
}: {
  readonly organizationId: string;
  readonly draft: Draft;
  readonly onClose: () => void;
}) {
  const [value, setValue] = useState<Draft>(draft);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const updateItem = (key: string, patch: Partial<DraftItem>) =>
    setValue((current) => ({ ...current, items: current.items.map((item) => (item.key === key ? { ...item, ...patch } : item)) }));
  const save = async () => {
    setBusy(true);
    setError(null);
    const outcome = await saveCiTemplate(organizationId, value.templateId, {
      name: value.name,
      documentType: value.documentType.trim() || null,
      description: value.description.trim() || null,
      items: value.items.map(({ key: _key, ...item }) => item),
    });
    setBusy(false);
    if (!outcome.ok) {
      setError(outcome.message);
      return;
    }
    toastManager.add({ type: "success", title: value.templateId ? "Template saved" : "Template created", description: outcome.value.name });
    onClose();
  };
  return (
    <Dialog open onOpenChange={(open) => (!open && !busy ? onClose() : undefined)}>
      <DialogPopup className="flex max-h-[88vh] max-w-2xl flex-col">
        <DialogHeader>
          <DialogTitle>{value.templateId ? "Edit template" : "New template"}</DialogTitle>
          <DialogDescription>Each requirement is checked against every clause. A miss is reported at the severity you set.</DialogDescription>
        </DialogHeader>
        <DialogPanel className="min-h-0 flex-1 space-y-4 overflow-y-auto">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="space-y-1.5">
              <span className="text-xs font-medium">Name</span>
              <Input value={value.name} onChange={(event) => setValue({ ...value, name: event.currentTarget.value })} placeholder="Side letter standard" />
            </label>
            <label className="space-y-1.5">
              <span className="text-xs font-medium">Document type</span>
              <Input value={value.documentType} onChange={(event) => setValue({ ...value, documentType: event.currentTarget.value })} placeholder="Side letter" />
            </label>
          </div>
          <label className="block space-y-1.5">
            <span className="text-xs font-medium">Description</span>
            <Input value={value.description} onChange={(event) => setValue({ ...value, description: event.currentTarget.value })} placeholder="What this standard is for" />
          </label>
          <div className="space-y-2">
            <span className="text-xs font-medium">Requirements</span>
            <ol className="space-y-2">
              {value.items.map((item, index) => (
                <li key={item.key} className="space-y-2 rounded-xl border border-border/70 bg-card p-3">
                  <div className="flex items-center gap-2">
                    <span className="w-5 text-xs tabular-nums text-muted-foreground">{index + 1}</span>
                    <Input
                      aria-label={`Requirement ${index + 1} title`}
                      value={item.title}
                      onChange={(event) => updateItem(item.key, { title: event.currentTarget.value })}
                      placeholder="Short title"
                      className="flex-1"
                    />
                    <Select value={item.severity} onValueChange={(next) => updateItem(item.key, { severity: (next ?? "medium") as CiSeverity })}>
                      <SelectTrigger size="sm" className="w-32" aria-label={`Requirement ${index + 1} severity`}>
                        <span className="flex items-center gap-1.5">
                          <SeverityGlyph severity={item.severity} />
                          {SEVERITY_LABEL[item.severity]}
                        </span>
                      </SelectTrigger>
                      <SelectContent>
                        {CI_SEVERITIES.map((severity) => (
                          <SelectItem key={severity} value={severity}>
                            <span className="flex items-center gap-1.5">
                              <SeverityGlyph severity={severity} />
                              {SEVERITY_LABEL[severity]}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      aria-label={`Remove requirement ${index + 1}`}
                      disabled={value.items.length === 1}
                      onClick={() => setValue((current) => ({ ...current, items: current.items.filter((entry) => entry.key !== item.key) }))}
                    >
                      <XIcon aria-hidden />
                    </Button>
                  </div>
                  <Textarea
                    aria-label={`Requirement ${index + 1} text`}
                    value={item.requirement}
                    onChange={(event) => updateItem(item.key, { requirement: event.currentTarget.value })}
                    placeholder="What the document must say, or must not say"
                    className="ms-7 min-h-16 w-[calc(100%-1.75rem)] text-[13px]"
                  />
                </li>
              ))}
            </ol>
            <Button
              size="sm"
              variant="ghost"
              className="gap-1.5"
              onClick={() => setValue((current) => ({ ...current, items: [...current.items, { key: newKey(), title: "", requirement: "", severity: "medium" }] }))}
            >
              <PlusIcon aria-hidden className="size-3.5" />
              Add requirement
            </Button>
          </div>
          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}
        </DialogPanel>
        <DialogFooter>
          <DialogClose render={<Button size="sm" variant="ghost" disabled={busy}>Cancel</Button>} />
          <Button size="sm" onClick={() => void save()} disabled={busy}>
            {busy ? "Saving…" : "Save template"}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}

function TemplateCard({
  template,
  canManage,
  onEdit,
  onRemove,
}: {
  readonly template: CiTemplate;
  readonly canManage: boolean;
  readonly onEdit: () => void;
  readonly onRemove: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <li className="overflow-hidden rounded-xl border border-border/70 bg-card" data-ci-template={template.id}>
      <div className="flex items-start gap-3 px-4 py-3">
        <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <ClipboardListIcon aria-hidden className="size-4" />
        </span>
        <button type="button" className="min-w-0 flex-1 text-left outline-none focus-visible:underline" onClick={() => setOpen(!open)} aria-expanded={open}>
          <span className="block truncate text-[13px] font-medium">{template.name}</span>
          <span className="block truncate text-xs text-muted-foreground">
            {[template.documentType, `${template.items.length} requirement${template.items.length === 1 ? "" : "s"}`].filter(Boolean).join(" · ")}
          </span>
          {template.description ? <span className="mt-1 block text-xs text-muted-foreground">{template.description}</span> : null}
        </button>
        {canManage ? (
          <div className="flex shrink-0 items-center gap-0.5">
            <Button size="icon-sm" variant="ghost" aria-label={`Edit ${template.name}`} onClick={onEdit}>
              <PencilIcon aria-hidden />
            </Button>
            <Button size="icon-sm" variant="ghost" aria-label={`Remove ${template.name}`} onClick={onRemove}>
              <Trash2Icon aria-hidden />
            </Button>
          </div>
        ) : null}
      </div>
      {open ? (
        <ol className="divide-y divide-border/50 border-t border-border/60">
          {template.items.map((item) => (
            <li key={item.id} className="flex gap-3 px-4 py-2.5">
              <SeverityGlyph severity={item.severity} className="mt-0.5" />
              <span className="min-w-0">
                <span className="block text-[13px] font-medium">{item.title}</span>
                <span className="block text-xs text-muted-foreground">{item.requirement}</span>
              </span>
            </li>
          ))}
        </ol>
      ) : null}
    </li>
  );
}

export function CiTemplatesPage() {
  const { organizationId, canManage } = useCiOrg();
  const templates = useCiTemplates(organizationId);
  const [editing, setEditing] = useState<Draft | null>(null);
  // Removing is for everyone in the organization: it waits for a confirmation.
  const [removing, setRemoving] = useState<CiTemplate | null>(null);
  const list = templates.read?.kind === "ready" ? templates.read.value : null;
  const remove = async (template: CiTemplate) => {
    if (!organizationId) return;
    const outcome = await archiveCiTemplate(organizationId, template.id);
    if (outcome.ok) setRemoving(null);
    toastManager.add(
      outcome.ok
        ? { type: "success", title: "Template removed", description: `${template.name} is no longer offered for new reviews.` }
        : { type: "error", title: "Template not removed", description: outcome.message },
    );
  };

  return (
    <CiPage
      title="Templates"
      meta={list ? list.length : undefined}
      data-ci-page="templates"
      actions={
        canManage ? (
          <Button size="sm" className="gap-1.5" onClick={() => setEditing(blank())}>
            <PlusIcon aria-hidden className="size-3.5" />
            New template
          </Button>
        ) : null
      }
    >
      {templates.read === null ? (
        <CiListSkeleton rows={4} />
      ) : templates.read.kind !== "ready" ? (
        <CiUnavailable what="Templates" onRetry={templates.reload} />
      ) : (
        <div className="mx-auto w-full max-w-3xl space-y-6 px-4 py-6 sm:px-6">
          <p className="text-sm text-muted-foreground">
            A template is your own standard for a kind of document. Choose one when you start a review and every requirement is
            checked clause by clause.{canManage ? "" : " Only admins can change templates."}
          </p>
          {list && list.length > 0 ? (
            <ul className="space-y-2">
              {list.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  canManage={canManage}
                  onEdit={() => setEditing(fromTemplate(template))}
                  onRemove={() => setRemoving(template)}
                />
              ))}
            </ul>
          ) : (
            <CiEmpty
              title="No templates yet"
              description={canManage ? "Start from a common standard and adapt it, or write your own." : "An admin can add your organization's standards here."}
            >
              {canManage ? (
                <div className={cn("grid w-full gap-2 sm:grid-cols-2")}>
                  {STARTERS.map((starter) => (
                    <button
                      key={starter.name}
                      type="button"
                      onClick={() => setEditing({ ...starter, templateId: null, items: starter.items.map((item) => ({ ...item, key: newKey() })) })}
                      className="rounded-xl border border-border/70 bg-card px-3 py-2.5 text-left outline-none transition-colors hover:bg-accent/40 focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <span className="block text-[13px] font-medium">{starter.name}</span>
                      <span className="block text-xs text-muted-foreground">{starter.items.length} requirements · edit before saving</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </CiEmpty>
          )}
        </div>
      )}
      {editing && organizationId ? <TemplateEditor organizationId={organizationId} draft={editing} onClose={() => setEditing(null)} /> : null}
      {removing ? (
        <CiConfirmDialog
          title="Remove this template?"
          description={<>“{removing.name}” is no longer offered for new reviews, for everyone in your organization.</>}
          confirmLabel="Remove template"
          onClose={() => setRemoving(null)}
          onConfirm={() => remove(removing)}
        />
      ) : null}
    </CiPage>
  );
}
